/**
 * app/components/FormTextInput.tsx
 * ---------------------------------
 * What: Themed text input with label, error states, and consistent spacing.
 * Why: Avoids repeating input wiring and style boilerplate across screens.
 */
import React, { useMemo, forwardRef } from 'react';
import { Platform, TextInput as RNTextInput, TextInputProps } from 'react-native';

// Counter to ensure unique IDs when the caller does not provide one.
let _counter = 0;

/**
 * A thin wrapper around React-Native's TextInput that ensures every instance
 * receives a stable `nativeID` (and `name` on the web) so that browsers can
 * correctly identify the field for autofill and accessibility purposes.
 *
 * The component accepts all regular TextInputProps. You may optionally pass
 * `nativeID` or `name` yourself – if omitted, the wrapper will generate a
 * deterministic ID ("input-<n>").
 *
 * We use `React.forwardRef` so that parents can attach refs to this component
 * and access imperative methods (e.g. `focus`, `measureLayout`).
 */
const FormTextInput = forwardRef<any, TextInputProps & { name?: string }>((props, ref) => {
  const { nativeID, name, ...rest } = props as any;

  // Lazily generate an ID only once per component instance.
  const generatedId = useMemo(() => nativeID || `input-${_counter++}`, [nativeID]);

  // Merge any platform-specific props.
  const platformProps = Platform.OS === 'web' ? ({ name: name || generatedId } as any) : {};

  return (
    <RNTextInput
      ref={ref as any}
      {...rest}
      nativeID={generatedId}
      {...platformProps}
    />
  );
});

FormTextInput.displayName = 'FormTextInput';

export default FormTextInput; 